// commonGameFunctions.ts
import { TObstacle } from '../../type/obstacle';
import { TCharacter } from '../../type/character';
import { TScene } from '../../type/scene';
import { roundToNearest } from '../../../../utils/gen/calcul';


export function pureTeleportTo(character: TCharacter, scene: TScene, position: string) {
    let finalX = 0;
    let finalY = 0;

    switch (position) {
        case 'TOP_LEFT':
            finalX = 0;
            finalY = 0;
            break;
        case 'TOP':
            finalX = (scene.width - character.width) / 2;
            finalY = 0;
            break;
        case 'TOP_RIGHT':
            finalX = scene.width - character.width;
            finalY = 0;
            break;
        case 'CENTER_LEFT':
            finalX = 0;
            finalY = (scene.height - character.height) / 2;
            break;
        case 'CENTER':
            finalX = (scene.width - character.width) / 2;
            finalY = (scene.height - character.height) / 2;
            break;
        case 'CENTER_RIGHT':
            finalX = scene.width - character.width;
            finalY = (scene.height - character.height) / 2;
            break;
        case 'BOTTOM_LEFT':
            finalX = 0;
            finalY = scene.height - character.height;
            break;
        case 'BOTTOM':
            finalX = (scene.width - character.width) / 2;
            finalY = scene.height - character.height;
            break;
        case 'BOTTOM_RIGHT':
            finalX = scene.width - character.width;
            finalY = scene.height - character.height;
            break;
        default:
            // On retourne les mêmes coords s'il y a une position inconnue
            break;
    }

    return {
        x: finalX,
        y: finalY,
        scale: character.scale,
    };
}


const pureAdjustDistanceForAngle = (angle: number, baseDistance: number): number => {
    // Normaliser l'angle entre 0 et 360 degrés
    const normalizedAngle = ((angle % 360) + 360) % 360;

    // Angles diagonaux (45°, 135°, 225°, 315°)
    const diagonalAngles = [45, 135, 225, 315];

    if (diagonalAngles.includes(normalizedAngle)) {
        // Multiplier la distance par √2 pour les déplacements diagonaux
        return baseDistance * Math.SQRT2;
    } else {
        // Garder la distance de base pour les autres angles
        return baseDistance;
    }
};


export function pureCheckCollision(
    nextX: number,
    nextY: number,
    character: TCharacter,
    scene: TScene,
    obstacles: TObstacle[]
) {
    let isColliding = false;
    let adjustedX = nextX;
    let adjustedY = nextY;

    // Vérifier les collisions avec les obstacles
    for (const obstacle of obstacles) {
        if (
            nextX < obstacle.x + obstacle.width &&
            nextX + character.width > obstacle.x &&
            nextY < obstacle.y + obstacle.height &&
            nextY + character.height > obstacle.y
        ) {
            isColliding = true;

            const fromLeft = character.x + character.width <= obstacle.x;
            const fromRight = character.x >= obstacle.x + obstacle.width;
            const fromTop = character.y + character.height <= obstacle.y;
            const fromBottom = character.y >= obstacle.y + obstacle.height;

            if (fromLeft) {
                adjustedX = obstacle.x - character.width;
            } else if (fromRight) {
                adjustedX = obstacle.x + obstacle.width;
            } else if (fromTop) {
                adjustedY = obstacle.y - character.height;
            } else if (fromBottom) {
                adjustedY = obstacle.y + obstacle.height;
            }
            break;
        }
    }

    // Vérifier les collisions avec les limites de la scène
    if (nextX < 0) {
        adjustedX = 0;
        isColliding = true;
    } else if (nextX + character.width > scene.width) {
        adjustedX = scene.width - character.width;
        isColliding = true;
    }

    if (nextY < 0) {
        adjustedY = 0;
        isColliding = true;
    } else if (nextY + character.height > scene.height) {
        adjustedY = scene.height - character.height;
        isColliding = true;
    }

    return { isColliding, adjustedX, adjustedY };
}



export function pureMoveCharacter(
    character: TCharacter,
    angle: number,
    distance: number,
    scene: TScene,
    obstacles: TObstacle[]
) {
    // Implement stepped movement to avoid tunneling through obstacles.
    // We advance in chunks of the character's displacement unit (adjusted per angle),
    // checking collision at each step and stopping at the first obstacle/boundary hit.

    const radians = (angle * Math.PI) / 180;

    // Total distance accounting for diagonal adjustment
    const totalAdjustedDistance = pureAdjustDistanceForAngle(angle, distance);

    // Step size base = min(requested distance, character unit)
    const stepBase = Math.min(distance, character.displacement_unit ?? distance);
    const stepAdjustedBase = pureAdjustDistanceForAngle(angle, stepBase);

    let remaining = totalAdjustedDistance;
    let currentX = character.x;
    let currentY = character.y;
    let collided = false;

    while (remaining > 0 && !collided) {
        const stepAdjusted = Math.min(stepAdjustedBase, remaining);
        const stepDX = stepAdjusted * Math.cos(radians);
        const stepDY = stepAdjusted * Math.sin(radians);

        const attemptX = roundToNearest(currentX + stepDX);
        const attemptY = roundToNearest(currentY + stepDY);

        // Provide previous position to collision checker to compute side properly
        const prevChar: TCharacter = { ...character, x: currentX, y: currentY };
        const { isColliding, adjustedX, adjustedY } = pureCheckCollision(
            attemptX,
            attemptY,
            prevChar,
            scene,
            obstacles
        );

        if (isColliding) {
            currentX = adjustedX;
            currentY = adjustedY;
            collided = true;
            break;
        }

        currentX = attemptX;
        currentY = attemptY;
        remaining -= stepAdjusted;
    }

    const finalX = roundToNearest(currentX);
    const finalY = roundToNearest(currentY);
    const totalDX = finalX - character.x;
    const totalDY = finalY - character.y;

    return {
        finalX,
        finalY,
        scale: totalDX >= 0 ? 1 : -1,
        isColliding: collided,
        deltaX: totalDX,
        deltaY: totalDY,
    };
}
